import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  /**
   * The page's history.scrollRestoration before the drawer set it to 'manual',
   * or null when the drawer hasn't touched it. The drawer's pushState happens
   * while the body is scroll-locked (position: fixed collapses the document's
   * real scrollTop to 0), so the browser snapshots the pre-cart history entry
   * at scroll 0. With 'auto' restoration, the history.back() on close re-applies
   * that poisoned snapshot AFTER closeDialog() has already restored the real
   * position — jumping the page to the top. 'manual' for the duration of the
   * open→close transaction leaves closeDialog()'s scrollTo as the only writer.
   * @type {'auto' | 'manual' | null}
   */
  #previousScrollRestoration = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
    // Never leave the page stuck in 'manual' if we're torn down mid-transaction
    // (e.g. a section re-render while the drawer is open).
    this.#restoreScrollRestoration();
  }

  /**
   * Pushes the cartDrawerOpen history entry BEFORE the scroll-lock engages.
   * showDialog() sets body{position:fixed}, which collapses the document's
   * real scrollTop to 0 — pushing after that (as #handleHistoryOpen used to)
   * makes the browser snapshot the pre-cart entry at scroll 0, and engines
   * that ignore scrollRestoration='manual' on same-document traversals
   * (WebKit) re-apply that 0 on close no matter what we do. Pushing here,
   * while the document still holds its real offset, makes the snapshot
   * correct — so even a forced restore lands where the shopper was.
   */
  showDialog() {
    if (isMobileBreakpoint() && !this.refs.dialog?.open && !history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }
    super.showDialog();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (this.#previousScrollRestoration == null) {
      this.#previousScrollRestoration = history.scrollRestoration;
    }
    history.scrollRestoration = 'manual';

    // Normally pushed by showDialog() before the scroll-lock; this is only a
    // fallback for an open that bypassed it (state already present otherwise).
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      // Restore scrollRestoration only after this traversal completes: the
      // browser reads the mode while performing the back(), and its scroll
      // re-application can land after the popstate dispatch.
      window.addEventListener('popstate', this.#restoreScrollRestoration, { once: true });
      history.back();
    } else {
      // Closed by the back gesture itself (#handlePopState) — the entry is
      // already popped. Defer past the traversal's rendering steps so a
      // late-applied restore can't still read 'auto'.
      requestAnimationFrame(() => requestAnimationFrame(this.#restoreScrollRestoration));
    }
  };

  #restoreScrollRestoration = () => {
    if (this.#previousScrollRestoration == null) return;
    history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // No summary to pin (e.g. empty cart): let the whole content scroll as one.
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    // Whenever a summary exists, keep it pinned to the bottom so the checkout CTA
    // stays visible while the items list scrolls between the free-shipping bar and
    // the summary. (Previously this un-pinned the summary — letting the whole
    // content scroll away, footer included — once the summary grew past ~half the
    // drawer height; that hid the checkout button on shorter viewports.)
    dialog.setAttribute('cart-summary-sticky', 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
